import Loading from "@/common/components/ui/loading/Loading";
import GenericTable from "@/common/components/ui/table/GenericTable";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import TableFooter from "@/common/components/ui/table/TableFooter";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { getCompanyId } from "@/common/services/company.api";
import { useEffect, useState } from "react";
import { formatDate } from "@/common/helpers/date.helpers";
import { numberToCurrency } from "@/common/helpers/currency.helpers";
import { IinfractionTicket } from "@/types/infractionTickets.type";
import { getInfractionTickets } from "@/common/services/infractionTickets.api";
import ModalCreateInfractionTickets from "../modals/ModalCreateInfractionTickets";
import { useCompanyStore } from "@/common/states/store/Company";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { IVehicle } from "@/types/vehicle.type";

const EMPTY_ACTION = { eventName: "", data: undefined };
const statusOptions = {
  PENDING: "Pendente",
  OVERDUE: "Vencido",
  PAID: "Pago",
};
const headers = {
  createdAt: "Data de Criação",
  infractionDate: "Data da Infração",
  dueDate: "Data do vencimento",
  licensePlate: "Placa",
  trafficTicket: "Auto de Infração",
  status: "Status",
  value: "Valor",
};

function TableInfractionTickets() {
  const companyId = getCompanyId();
  const company = useCompanyStore();
  const { notifyError } = useToastContext();
  const navigate = useNavigate();

  const [infractionTicketData, setInfractionTicketData] = useState<
    IinfractionTicket[]
  >([]);
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    situation: "",
    orderBy: "-createdAt",
  });

  // Função para carregar os dados da API
  const loadInfractionTickets = async (filters, page) => {
    setIsLoading(true);
    setInfractionTicketData([]);
    try {
      const currentFilters = { ...filters };
      const currentPage = page;

      const apiParams = {
        ...currentFilters,
        page: currentPage,
        companyId,
        startDate: currentFilters.startDate?.toISOString(),
        endDate: currentFilters.endDate?.toISOString(),
        startDateAtualizadoEm: currentFilters.startDateAtualizadoEm?.toISOString(),
        endDateAtualizadoEm: currentFilters.endDateAtualizadoEm?.toISOString(),
      };

      const result = await getInfractionTickets(apiParams);

      if (result.ok) {
        setData({
          itemsPerPage: data.itemsPerPage,
          totalItems: result.totalItems,
        });
        setInfractionTicketData(result.data);
      } else {
        notifyError(
          (result as { message?: string })?.message ||
          "Erro desconhecido ao buscar infrações."
        );
      }
    } catch (err) {
      console.log(err);
      notifyError("Falha ao se comunicar com o serviço de infrações.");
    } finally {
      setIsLoading(false);
    }
  };

  // Carrega os dados quando a página ou os filtros mudam
  useEffect(() => {
    loadInfractionTickets(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, companyId, company]);

  const isAction = (eventName: string) => action.eventName === eventName;

  // Função chamada quando o modal é fechado para recarregar os dados
  const cleanActionData = () => {
    setAction(EMPTY_ACTION);
    loadInfractionTickets(filters, page); // Recarrega a lista
  };

  return (
    <div className="table-section">
      <TableFilters
        dashboardHeader="Boletos de Infrações"
        placeholder="Digite a placa ou o auto de infração"
        newItemLabel="Processar Novo Boleto"
        onNewItemClick={() =>
          setAction({ eventName: "createInfractionTickets", data: undefined })
        }
        onSearch={setFilters}
        entity="loan"
        tableData={infractionTicketData}
        statusOptions={statusOptions}
      />

      <GenericTable
        headers={headers}
        data={infractionTicketData}
        setFilters={({ orderBy }) => {
          setFilters((prevFilters) => ({
            ...prevFilters,
            orderBy,
          }));
        }}
        renders={
          {
            createdAt: formatDate,
            infractionDate: formatDate,
            dueDate: formatDate,
            licensePlate: (licensePlate, vehicle) => (
              <a
                href={`/veiculos/${(vehicle as IVehicle).licensePlate}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/veiculos/${(vehicle as IVehicle).licensePlate}`);
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {licensePlate as string}
              </a>
            ),
            value: (stringValue) => numberToCurrency(parseFloat(stringValue)),
            trafficTicket: (trafficTicket, data) => {
              const trafficFinePayment =
                !!data.attached &&
                !!data.details?.barcode &&
                !!data.payment?.billId;

              return (
                <span
                  className="flex items-center justify-center gap-2"
                  style={{ fontSize: "15px" }}
                >
                  {trafficFinePayment ? (
                    <FaCheckCircle className="text-greenPrimary" size={14} />
                  ) : (
                    <FaTimesCircle className="text-redPrimary" size={14} />
                  )}
                  <a
                    href={`/relatorios/multas/${trafficTicket}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/relatorios/multas/${trafficTicket}`);
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {trafficTicket}
                  </a>
                </span>
              );
            },
            status: (status) => {
              const styles = {
                PENDING: "bg-yellow-200 text-yellow-800",
                OVERDUE: "bg-red-200 text-red-800",
                PAID: "bg-green-200 text-green-800",
              };
              const text = {
                PENDING: "Pendente",
                OVERDUE: "Vencido",
                PAID: "Pago",
              };
              return (
                <span
                  className={`px-2 py-1 rounded-full text-xs font-semibold ${styles[status as keyof typeof styles] || "bg-gray-200"
                    }`}
                >
                  {text[status as keyof typeof text] || status}
                </span>
              );
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any
        }
        entity="loan"
      />

      {isLoading && <Loading />}

      <TableFooter
        page={page || 1}
        totalItems={data.totalItems || 0}
        itemsPerPage={+(filters.itemsPerPage ?? 10)}
        goToPage={setPage}
      />

      {isAction("createInfractionTickets") && (
        <ModalCreateInfractionTickets onClose={cleanActionData} />
      )}
    </div>
  );
}

export default TableInfractionTickets;
